import { formatAmount } from '@/lib/format-number'
import type { LoyaltyCurrencyBenefit, LoyaltyFreespinBenefit, LoyaltyPercentBenefit, LoyaltyTier } from '@/types/loyalty'

export interface LoyaltyBenefitDisplay {
	label: string
	value: string
}

function hasPositiveNumericValue(value: string | number | null | undefined): boolean {
	if (value === null || value === undefined) {
		return false
	}

	if (typeof value === 'string' && value.trim() === '') {
		return false
	}

	const numericValue = typeof value === 'string' ? Number.parseFloat(value) : value

	return Number.isFinite(numericValue) && numericValue > 0
}

function formatPercentValue(value: string): string {
	const numericValue = Number.parseFloat(value)

	if (!Number.isFinite(numericValue)) {
		return '0%'
	}

	const formattedValue = numericValue % 1 === 0 ? numericValue.toFixed(0) : String(numericValue)

	return `${formattedValue}%`
}

function formatPercentBenefit(benefit: LoyaltyPercentBenefit): string {
	return formatPercentValue(benefit.percent)
}

function formatCurrencyBenefit(benefit: LoyaltyCurrencyBenefit): string {
	return `${formatAmount(benefit.amount)} ${benefit.currency_code}`
}

function formatFreespinBenefit(benefit: LoyaltyFreespinBenefit): string {
	const count = typeof benefit.count === 'string' ? Number.parseFloat(benefit.count) : benefit.count
	const formattedCount = Number.isFinite(count) ? formatAmount(count, 0) : '0'

	return `${formattedCount} FS`
}

export function getLoyaltyTierBenefits(tier: LoyaltyTier): LoyaltyBenefitDisplay[] {
	const benefits: LoyaltyBenefitDisplay[] = []

	if (tier.daily_rakeback && hasPositiveNumericValue(tier.daily_rakeback.percent)) {
		benefits.push({ label: 'Daily Rakeback', value: formatPercentBenefit(tier.daily_rakeback) })
	}

	if (tier.weekly_cashback && hasPositiveNumericValue(tier.weekly_cashback.percent)) {
		benefits.push({ label: 'Weekly Cashback', value: formatPercentBenefit(tier.weekly_cashback) })
	}

	if (tier.monthly_cashback && hasPositiveNumericValue(tier.monthly_cashback.percent)) {
		benefits.push({ label: 'Monthly Cashback', value: formatPercentBenefit(tier.monthly_cashback) })
	}

	if (tier.daily_bonus && hasPositiveNumericValue(tier.daily_bonus.amount)) {
		benefits.push({ label: 'Daily Bonus', value: formatCurrencyBenefit(tier.daily_bonus) })
	}

	if (tier.weekly_bonus && hasPositiveNumericValue(tier.weekly_bonus.amount)) {
		benefits.push({ label: 'Weekly Bonus', value: formatCurrencyBenefit(tier.weekly_bonus) })
	}

	if (tier.monthly_bonus && hasPositiveNumericValue(tier.monthly_bonus.amount)) {
		benefits.push({ label: 'Monthly Bonus', value: formatCurrencyBenefit(tier.monthly_bonus) })
	}

	if (tier.daily_freespin && hasPositiveNumericValue(tier.daily_freespin.count)) {
		benefits.push({ label: 'Daily Free Spins', value: formatFreespinBenefit(tier.daily_freespin) })
	}

	if (tier.weekly_freespin && hasPositiveNumericValue(tier.weekly_freespin.count)) {
		benefits.push({ label: 'Weekly Free Spins', value: formatFreespinBenefit(tier.weekly_freespin) })
	}

	if (tier.monthly_freespin && hasPositiveNumericValue(tier.monthly_freespin.count)) {
		benefits.push({ label: 'Monthly Free Spins', value: formatFreespinBenefit(tier.monthly_freespin) })
	}

	if (tier.level_up_bonus && hasPositiveNumericValue(tier.level_up_bonus.amount)) {
		benefits.push({ label: 'Level Up Bonus', value: formatCurrencyBenefit(tier.level_up_bonus) })
	}

	if (tier.level_up_freespin && hasPositiveNumericValue(tier.level_up_freespin.count)) {
		benefits.push({ label: 'Level Up Free Spins', value: formatFreespinBenefit(tier.level_up_freespin) })
	}

	if (hasPositiveNumericValue(tier.daily_withdrawal_limit)) {
		benefits.push({
			label: 'Daily Withdrawal Limit',
			value: formatAmount(tier.daily_withdrawal_limit, 0),
		})
	}

	return benefits
}

export function formatRequiredPointsRange(tiers: LoyaltyTier[], tierIndex: number): string {
	const currentTier = tiers[tierIndex]
	const nextTier = tiers[tierIndex + 1]
	const minPoints = formatAmount(currentTier.required_points, 0)

	if (!nextTier) {
		return `${minPoints}+`
	}

	const maxPoints = formatAmount(Math.max(nextTier.required_points - 1, currentTier.required_points), 0)

	return `${minPoints}-${maxPoints}`
}

export function sortActiveLoyaltyTiers(tiers: LoyaltyTier[]): LoyaltyTier[] {
	return tiers.filter((tier) => tier.is_active).sort((left, right) => left.order - right.order)
}
